// ABOUTME: New analytics page with tabbed interface, time window selector, and placeholder card containers
// ABOUTME: Foundation for analytics overhaul - replaces existing AnalyticsPage with structured layout

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import SharedAppBar from './SharedAppBar';
import FamilySetupWizard from './FamilySetupWizard';
import WhoIsLoggingCard from './WhoIsLoggingCard';
import TodaysRecordingProgressCard from './TodaysRecordingProgressCard';
import TodaysPositivityCard from './TodaysPositivityCard';
import RecordingStreakCard from './RecordingStreakCard';
import DailyBehaviorsCard from './DailyBehaviorsCard';
import HeartCalendar from './HeartCalendar';
import AnalyticsTimeChart from './AnalyticsTimeChart';
import GoalsToCelebrateCard from './GoalsToCelebrateCard';
import MeltdownTrackerCard from './dashboard/MeltdownTrackerCard';
import TriangleTrackerCard from './dashboard/TriangleTrackerCard';
import OneOnOneTimeTrackerCard from './dashboard/OneOnOneTimeTrackerCard';
import WeeklyRecordingCard from './WeeklyRecordingCard';
import TimePatternsChart from './TimePatternsChart';
import PositiveNegativeBehaviorsChart from './PositiveNegativeBehaviorsChart';
import HeartSuccessRateCard from './HeartSuccessRateCard';
import { Family, Child, TodaysBehaviorCounts, MeltdownTrackerDto, TriangleTrackerDto, OneOnOneTimeTrackerDto } from '../types/api';
import { familyAPI, behaviorAPI, analyticsAPI, getCurrentAccessToken } from '../services/api';
import { getTodayInFamilyTimezone } from '../utils/dateUtils';
import { createMobilePageContainerSx, createMobileCardContentSx } from '../utils/mobileOptimization';

interface HeartSuccessRateData {
  familyId: number;
  totalChildren: number;
  childrenOnTrack: number;
  successPercentage: number;
  childStatuses: {
    childId: number;
    childName: string;
    initial: string;
    isOnTrack: boolean;
    status: string;
    behaviorCounts: {
      starCount: number;
      triangleCount: number;
      squareCount: number;
      makeItRightCount: number;
      netSquares: number;
    };
  }[];
  date: string;
  familyTimezone: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  'aria-labelledby'?: string;
  'data-time-window'?: string;
  'data-timezone'?: string;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      className="MuiGrid-container"
      {...other}
    >
      {value === index && (
        <Box sx={createMobilePageContainerSx()}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number, name: string) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
    'aria-label': `${name} analytics tab`
  };
}


const NewAnalyticsPage: React.FC = () => {
  const { user } = useAuth();
  const { Term } = useFamilyTerminology();
  const [families, setFamilies] = useState<Family[]>([]);
  const [selectedFamily, setSelectedFamily] = useState<Family | null>(null);
  const [familyDetails, setFamilyDetails] = useState<Family | null>(null); // Full family details with correct createdAt
  const [children, setChildren] = useState<Child[]>([]);
  const [familyTimezone, setFamilyTimezone] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [childHeartDates, setChildHeartDates] = useState<{[childId: number]: { heartDates: string[]; mendingHeartDates: string[] }}>({});
  const [childHeartLoading, setChildHeartLoading] = useState<{[childId: number]: boolean}>({});
  const [selectedDate, setSelectedDate] = useState<Date>(new Date()); // For timeline display
  const [todaysBehaviorCounts, setTodaysBehaviorCounts] = useState<TodaysBehaviorCounts | null>(null);
  const [behaviorCountsLoading, setBehaviorCountsLoading] = useState(false);
  const [meltdownData, setMeltdownData] = useState<Record<number, MeltdownTrackerDto>>({});
  const [triangleData, setTriangleData] = useState<Record<number, TriangleTrackerDto>>({});
  const [oneOnOneTimeData, setOneOnOneTimeData] = useState<Record<number, OneOnOneTimeTrackerDto>>({});
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [heartSuccessRateData, setHeartSuccessRateData] = useState<HeartSuccessRateData | null>(null);
  const [twelveWeekAverage, setTwelveWeekAverage] = useState<number | undefined>(undefined);
  const [behaviorPatternsData, setBehaviorPatternsData] = useState<{
    dayOfWeekAverages: {
      dayOfWeek: number;
      stars: number;
      triangles: number;
      squares: number;
      flipYourCoins: number;
      makeItRights: number;
      oneOnOneTimes: number;
      total: number;
    }[];
    dateRange: {
      startDate: string;
      endDate: string;
    };
    totalDays: number;
    totalBehaviors: number;
  } | null>(null);

  // Memoized callback for heart calendar month navigation
  const handleDateRangeChange = useCallback((startDate: Date, endDate: Date) => {
    // activeTab 0 is Family Overview, activeTab 1 is first child at index 0
    const child = activeTab > 0 ? children[activeTab - 1] : null;
    if (!child || !startDate || !endDate) return;
    const childId = child.id || child.childId;
    if (!childId) return;

    setChildHeartLoading(prev => ({ ...prev, [childId]: true }));

    behaviorAPI.getChildHeartDates(childId, startDate, endDate)
      .then(result => {
        setChildHeartDates(prev => ({
          ...prev,
          [childId]: { heartDates: result.heartDates || [], mendingHeartDates: result.mendingHeartDates || [] }
        }));
      })
      .catch(error => {
        console.error(`Failed to load heart data for child ${childId}:`, error);
        setChildHeartDates(prev => ({
          ...prev,
          [childId]: { heartDates: [], mendingHeartDates: [] }
        }));
      })
      .finally(() => {
        setChildHeartLoading(prev => ({ ...prev, [childId]: false }));
      });
  }, [children, activeTab]);

  useEffect(() => {
    if (user) {
      loadFamilyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadFamilyData intentionally omitted; it is stable and adding it would cause extra fetches
  }, [user]);

  useEffect(() => {
    if (selectedFamily) {
      loadChildren(selectedFamily.familyId);
      loadFamilyTimezone(selectedFamily.familyId);
      // Load full family details to get correct createdAt date
      loadFamilyDetails(selectedFamily.familyId);
    }
  }, [selectedFamily]);

  // Load behavior counts and behaviors after family timezone is available
  useEffect(() => {
    if (selectedFamily && familyTimezone) {
      loadTodaysBehaviorCounts(selectedFamily.familyId);
      loadBehaviorPatterns(selectedFamily.familyId);
      loadHeartSuccessRateData(selectedFamily.familyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load functions intentionally omitted; they are stable and adding them would cause extra fetches
  }, [selectedFamily, familyTimezone]);

  // Update selectedDate when family timezone is loaded
  useEffect(() => {
    if (familyTimezone) {
      // Set selectedDate to today in the family timezone
      const todayInFamilyTZ = getTodayInFamilyTimezone(familyTimezone || undefined);
      const [year, month, day] = todayInFamilyTZ.split('-').map(Number);
      setSelectedDate(new Date(year, month - 1, day)); // month is 0-indexed in Date constructor
    }
  }, [familyTimezone]);

  // Load heart data for selected child when tab changes
  useEffect(() => {
    const loadHeartDataForSelectedChild = async () => {
      // Only load for child tabs (activeTab > 0)
      if (activeTab === 0 || children.length === 0) return;
      
      const selectedChild = children[activeTab - 1]; // activeTab 1 = first child (index 0)
      if (!selectedChild) return;
      
      const childId = selectedChild.id || selectedChild.childId;
      if (!childId) return;
      
      // Check if we already have data for this child
      if (childHeartDates[childId] !== undefined) return;
      
      try {
        setChildHeartLoading(prev => ({ ...prev, [childId]: true }));
        
        // Use timezone utilities for month calculations
        const todayStr = getTodayInFamilyTimezone(familyTimezone || undefined);
        const [year, month] = todayStr.split('-').map(Number);
        const monthStart = new Date(year, month - 1, 1); // month is 0-indexed in Date constructor
        const monthEnd = new Date(year, month, 0); // Day 0 of next month = last day of current month
        
        const result = await behaviorAPI.getChildHeartDates(childId, monthStart, monthEnd);
        setChildHeartDates(prev => ({
          ...prev,
          [childId]: { heartDates: result.heartDates || [], mendingHeartDates: result.mendingHeartDates || [] }
        }));

        // Also load meltdown, triangle, and 1:1 time data for this child
        loadMeltdownData(childId);
        loadTriangleData(childId);
        loadOneOnOneTimeData(childId);
      } catch (error) {
        console.error(`Failed to load heart data for child ${childId}:`, error);
        setChildHeartDates(prev => ({
          ...prev,
          [childId]: { heartDates: [], mendingHeartDates: [] }
        }));
      } finally {
        setChildHeartLoading(prev => ({ ...prev, [childId]: false }));
      }
    };
    
    loadHeartDataForSelectedChild();
  }, [activeTab, children, childHeartDates, familyTimezone]);


  const loadFamilyData = async () => {
    try {
      setIsLoading(true);
      const familiesData = await familyAPI.getFamilies();
      setFamilies(familiesData);
      
      if (familiesData.length > 0 && !selectedFamily) {
        setSelectedFamily(familiesData[0]);
      }
      
      setError(null);
    } catch (error: unknown) {
      console.error('Failed to load family data:', error);
      setError('Error loading family data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadChildren = async (familyId: number) => {
    try {
      const childrenData = await familyAPI.getChildren(familyId);
      
      // Sort children youngest to oldest (by birth date descending)
      const sortedChildren = childrenData.sort((a, b) => {
        // Use dateOfBirth if available, otherwise construct from birthYear/birthMonth/birthDay
        const getChildBirthDate = (child: Child) => {
          if (child.dateOfBirth) {
            return new Date(child.dateOfBirth);
          }
          // Construct date from components (default month and day to 1 if missing)
          return new Date(child.birthYear, (child.birthMonth || 1) - 1, child.birthDay || 1);
        };
        
        const dateA = getChildBirthDate(a);
        const dateB = getChildBirthDate(b);
        return dateB.getTime() - dateA.getTime();
      });
      
      setChildren(sortedChildren);
    } catch (error) {
      console.error('Failed to load children:', error);
    }
  };

  const loadFamilyTimezone = async (familyId: number) => {
    try {
      const timezone = await familyAPI.getFamilyTimezone(familyId);
      setFamilyTimezone(timezone);
    } catch (error) {
      console.error('Failed to load family timezone:', error);
      // Set to undefined and let components handle gracefully - do not fallback to browser timezone
      setFamilyTimezone(undefined);
    }
  };

  const loadFamilyDetails = async (familyId: number) => {
    try {
      const details = await familyAPI.getFamilyDetails(familyId);
      setFamilyDetails(details);
    } catch (error) {
      console.error('Failed to load family details:', error);
    }
  };


  const loadTodaysBehaviorCounts = async (familyId: number) => {
    try {
      setBehaviorCountsLoading(true);
      const today = getTodayInFamilyTimezone(familyTimezone ?? undefined);
      
      const counts = await behaviorAPI.getTodaysBehaviorCountsByType(familyId, today);
      
      // Transform the data from Record<number, number> to TodaysBehaviorCounts
      const behaviorCounts: TodaysBehaviorCounts = {
        starCount: counts[1] || 0,        // BehaviorType.Star = 1
        triangleCount: counts[2] || 0,    // BehaviorType.Triangle = 2
        squareCount: counts[3] || 0,      // BehaviorType.Square = 3
        flipYourCoinCount: counts[4] || 0, // BehaviorType.FlipYourCoin = 4
        makeItRightCount: counts[5] || 0,  // BehaviorType.MakeItRight = 5
        oneOnOneTimeCount: counts[6] || 0, // BehaviorType.OneOnOneTime = 6
        resetCount: counts[8] || 0         // BehaviorType.Reset = 8
      };
      
      setTodaysBehaviorCounts(behaviorCounts);
    } catch (error) {
      console.error('Failed to load today\'s behavior counts:', error);
      // Set empty counts on error
      setTodaysBehaviorCounts({
        starCount: 0,
        triangleCount: 0,
        squareCount: 0,
        flipYourCoinCount: 0,
        makeItRightCount: 0,
        oneOnOneTimeCount: 0,
        resetCount: 0
      });
    } finally {
      setBehaviorCountsLoading(false);
    }
  };

  const loadBehaviorPatterns = async (familyId: number) => {
    try {
      setBehaviorLoading(true);
      // Fetch day-of-week patterns as default (Issue #1135)
      const patternsData = await analyticsAPI.getBehaviorPatternsByDayOfWeek(familyId, familyTimezone || undefined);
      setBehaviorPatternsData(patternsData);
    } catch (error) {
      console.error('Failed to load behavior patterns:', error);
      setBehaviorPatternsData(null);
    } finally {
      setBehaviorLoading(false);
    }
  };

  const loadHeartSuccessRateData = async (familyId: number) => {
    if (!familyId) return;

    try {
      const authToken = getCurrentAccessToken();

      // Load current heart success rate data
      const heartResponse = await fetch(`/api/heart-success-rate/family/${familyId}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (heartResponse.ok) {
        const heartData = await heartResponse.json();
        setHeartSuccessRateData(heartData);
      } else {
        console.error('Heart success rate API failed:', heartResponse.status, await heartResponse.text());
      }

      // Load twelve-week average
      const averageResponse = await fetch(`/api/heart-success-rate/family/${familyId}/twelve-week-average`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (averageResponse.ok) {
        const averageData = await averageResponse.json();
        setTwelveWeekAverage(averageData.twelveWeekAveragePercentage);
      } else {
        console.error('Twelve week average API failed:', averageResponse.status, await averageResponse.text());
      }
    } catch (error) {
      console.error('Failed to load heart success rate data:', error);
      // Silent failure for heart success rate data
    }
  };

  const loadMeltdownData = async (childId: number) => {
    try {
      const data = await behaviorAPI.getMeltdownTrackerData(childId);
      setMeltdownData(prev => ({
        ...prev,
        [childId]: data
      }));
    } catch (error) {
      console.error('Failed to load meltdown data:', error);
    }
  };

  const loadTriangleData = async (childId: number) => {
    try {
      const data = await behaviorAPI.getTriangleTrackerData(childId);
      setTriangleData(prev => ({
        ...prev,
        [childId]: data
      }));
    } catch (error) {
      console.error('Failed to load triangle data:', error);
    }
  };

  const loadOneOnOneTimeData = async (childId: number) => {
    try {
      const data = await behaviorAPI.getOneOnOneTimeTrackerData(childId);
      setOneOnOneTimeData(prev => ({
        ...prev,
        [childId]: data
      }));
    } catch (error) {
      console.error('Failed to load 1:1 time data:', error);
    }
  };

  const handleAppBarMenuClick = (action: string) => {
    if (action === '/analytics') {
      // Already on analytics page
    } else if (action === 'Dashboard') {
      window.location.href = '/';
    } else if (action.startsWith('/')) {
      window.location.href = action;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };



  // Show family setup if no families exist
  if (!isLoading && families.length === 0) {
    return <FamilySetupWizard />;
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar title="Analytics" onMenuClick={handleAppBarMenuClick} />
        <Box sx={createMobilePageContainerSx()}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar title="Analytics" onMenuClick={handleAppBarMenuClick} />
      
      <Box sx={createMobilePageContainerSx({ flex: 1 })}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading analytics...</Typography>
          </Box>
        ) : (
          <>

            {/* Tabbed Interface */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="Analytics tabs"
                className="MuiTabs-root"
              >
                <Tab label={Term} {...a11yProps(0, Term)} />
                {children.map((child, index) => (
                  <Tab 
                    key={child.id} 
                    label={child.name} 
                    {...a11yProps(index + 1, child.name)}
                  />
                ))}
              </Tabs>
            </Box>

            {/* Family Tab Content */}
            <TabPanel
              value={activeTab}
              index={0}
              aria-labelledby="analytics-tab-0"
              data-timezone={familyTimezone || undefined}
            >
              <Grid container spacing={3}>
                {/* Position 1: Heart Progress (Heart Success Rate) - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-heart-success-rate">
                  <HeartSuccessRateCard
                    familyId={selectedFamily?.familyId || 0}
                    familyData={heartSuccessRateData}
                    twelveWeekAverage={twelveWeekAverage}
                  />
                </Grid>

                {/* Position 2: Positivity Ratio (Today's Positivity) - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-positivity">
                  {todaysBehaviorCounts ? (
                    <TodaysPositivityCard
                      behaviorCounts={todaysBehaviorCounts}
                      loading={behaviorCountsLoading}
                    />
                  ) : (
                    <Card data-testid="family-card-placeholder-behavior-breakdown">
                      <CardContent>
                        <Typography variant="h6">Today&apos;s Positivity</Typography>
                        <Typography color="text.secondary">Loading...</Typography>
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                {/* Position 3: Today's Recording Progress - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-recording-progress">
                  <TodaysRecordingProgressCard
                    familyId={selectedFamily?.familyId || 0}
                    familyTimezone={familyTimezone || undefined}
                  />
                </Grid>

                {/* Position 4: Goals to Celebrate - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-goals-to-celebrate">
                  <GoalsToCelebrateCard
                    familyId={selectedFamily?.familyId || 0}
                  />
                </Grid>

                {/* Position 5: Behavior Patterns by Time (full width) - Issue #957 */}
                <Grid size={{ xs: 12 }} data-testid="family-card-time-heatmap">
                  <Card>
                    <CardContent>
                      <Typography variant="h6">⏰ Behavior Patterns by Time</Typography>
                      {behaviorLoading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress />
                        </Box>
                      ) : (
                        <TimePatternsChart
                          familyId={selectedFamily?.familyId || 0}
                          dayOfWeekAveragesData={behaviorPatternsData?.dayOfWeekAverages}
                          familyTimezone={familyTimezone || undefined}
                        />
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Position 6: Recording Streak - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-recording-streak">
                  <RecordingStreakCard
                    familyId={selectedFamily?.familyId || 0}
                  />
                </Grid>

                {/* Position 7: This Week's Recording (Weekly Recording) - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-weekly-recording">
                  <WeeklyRecordingCard
                    familyId={selectedFamily?.familyId || 0}
                    familyTimezone={familyTimezone || undefined}
                  >
                    {children}
                  </WeeklyRecordingCard>
                </Grid>

                {/* Position 8: Who's Logging - Issue #957 */}
                <Grid size={{ xs: 12, md: 6 }} data-testid="family-card-who-logging">
                  <WhoIsLoggingCard
                    familyId={selectedFamily?.familyId || 0}
                    timeWindow="Last 7 days"
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {/* Child Tab Content */}
            {children.map((child, index) => (
              <TabPanel
                key={child.id}
                value={activeTab}
                index={index + 1}
                aria-labelledby={`analytics-tab-${index + 1}`}
                data-timezone={familyTimezone || undefined}
              >
                <Grid container spacing={3}>
                  {/* Position 1: Daily Heart Success Rate - Issue #958 */}
                  <Grid size={{ xs: 12, md: 6 }} data-testid="child-card-heart-success-rate">
                    <HeartSuccessRateCard
                      familyId={selectedFamily?.familyId || 0}
                      childId={child.id || child.childId}
                      familyData={null}
                      twelveWeekAverage={undefined}
                    />
                  </Grid>

                  {/* Position 2: Daily Behaviors Card - Issue #958 */}
                  <Grid size={{ xs: 12, md: 6 }} data-testid="child-card-daily-behaviors">
                    <DailyBehaviorsCard
                      child={child}
                      familyId={selectedFamily?.familyId || 0}
                      timezone={familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                      timeWindow="Today"
                    />
                  </Grid>

                  {/* Position 3: Positive/Negative Behaviors Chart (full width) - Issue #958 */}
                  <Grid size={{ xs: 12 }} data-testid="child-card-positive-negative-behaviors">
                    <PositiveNegativeBehaviorsChart
                      child={child}
                      familyId={selectedFamily?.familyId || 0}
                      familyTimezone={familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                    />
                  </Grid>


                  {/* Position 4: Monthly Calendar & Daily Timeline (full width) - Issue #958 */}
                  <Grid size={{ xs: 12 }} data-testid="child-card-calendar-timeline">
                    <Card data-testid={`child-calendar-timeline-${child.id || child.childId}`}>
                      <CardContent sx={createMobileCardContentSx()}>
                        <Typography variant="h6" gutterBottom>
                          {child.name}&apos;s Monthly Calendar & Daily Timeline
                        </Typography>
                        <HeartCalendar
                          heartDates={childHeartDates[child.id || child.childId || 0]?.heartDates || []}
                          mendingHeartDates={childHeartDates[child.id || child.childId || 0]?.mendingHeartDates || []}
                          loading={childHeartLoading[child.id || child.childId || 0] || false}
                          viewMode="month"
                          familyTimezone={familyTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
                          familyCreatedAt={familyDetails?.createdAt}
                          selectedDate={selectedDate}
                          onDaySelect={(date) => {
                            setSelectedDate(date);
                          }}
                          onDateRangeChange={handleDateRangeChange}
                        />

                        {/* Selected Date Display */}
                        <Box sx={{ mt: 3, mb: 2, textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
                            {(() => {
                              const formatFullDate = (date: Date, familyTimezone?: string): string => {
                                // Helper function to get ordinal suffix
                                const getOrdinalSuffix = (day: number): string => {
                                  if (day > 3 && day < 21) return 'th';
                                  switch (day % 10) {
                                    case 1: return 'st';
                                    case 2: return 'nd';
                                    case 3: return 'rd';
                                    default: return 'th';
                                  }
                                };

                                const day = date.getDate();
                                const weekdayOptions: Intl.DateTimeFormatOptions = {
                                  weekday: 'long',
                                  timeZone: familyTimezone || 'UTC'
                                };
                                const monthOptions: Intl.DateTimeFormatOptions = {
                                  month: 'long',
                                  timeZone: familyTimezone || 'UTC'
                                };
                                const yearOptions: Intl.DateTimeFormatOptions = {
                                  year: 'numeric',
                                  timeZone: familyTimezone || 'UTC'
                                };

                                const weekday = date.toLocaleDateString('en-US', weekdayOptions);
                                const monthName = date.toLocaleDateString('en-US', monthOptions);
                                const yearStr = date.toLocaleDateString('en-US', yearOptions);
                                const ordinalSuffix = getOrdinalSuffix(day);

                                return `${weekday}, ${monthName} ${day}${ordinalSuffix}, ${yearStr}`;
                              };

                              return formatFullDate(selectedDate, familyTimezone || undefined);
                            })()}
                          </Typography>
                        </Box>

                        {/* Daily Timeline Chart */}
                        <Box sx={{ mt: 2 }}>
                          <AnalyticsTimeChart
                            child={child}
                            familyId={selectedFamily?.familyId || 0}
                            selectedDate={selectedDate.getFullYear() + '-' + (selectedDate.getMonth() + 1).toString().padStart(2, '0') + '-' + selectedDate.getDate().toString().padStart(2, '0')}
                            familyTimezone={familyTimezone || undefined}
                            isAnonymousAccess={false}
                            hideTitle={true}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Position 5: Behavior Tracking by Time (full width) - Issue #958 */}
                  <Grid size={{ xs: 12 }} data-testid="child-card-behavior-patterns">
                    <Card>
                      <CardContent sx={createMobileCardContentSx()}>
                        <Typography variant="h6">⏰ {child.name}&apos;s Behavior Patterns by Time</Typography>
                        {behaviorLoading ? (
                          <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress />
                          </Box>
                        ) : (
                          <TimePatternsChart
                            familyId={selectedFamily?.familyId || 0}
                            childId={child.id || child.childId}
                            childName={child.name}
                            familyTimezone={familyTimezone || undefined}
                            testId="time-patterns-chart"
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Position 6: Triangle Tracker - Issue #958 */}
                  <Grid size={{ xs: 12, md: 6 }} data-testid="child-card-triangle-tracker">
                    {(child.id || child.childId) && triangleData[(child.id || child.childId) as number] ? (
                      <TriangleTrackerCard
                        data={triangleData[(child.id || child.childId) as number]}
                      />
                    ) : (
                      <Card data-testid="child-card-placeholder-daily-triangles">
                        <CardContent>
                          <Typography variant="h6">Daily Struggle (Triangles) vs Developmental Averages</Typography>
                          <Typography color="text.secondary">Loading triangle data...</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>

                  {/* Position 7: Meltdown Tracker - Issue #958 */}
                  <Grid size={{ xs: 12, md: 6 }} data-testid="child-card-meltdown-tracker">
                    {(child.id || child.childId) && meltdownData[(child.id || child.childId) as number] ? (
                      <MeltdownTrackerCard
                        data={meltdownData[(child.id || child.childId) as number]}
                      />
                    ) : (
                      <Card data-testid="child-card-placeholder-weekly-meltdowns">
                        <CardContent>
                          <Typography variant="h6">Weekly Meltdowns (Squares)</Typography>
                          <Typography color="text.secondary">Loading meltdown data...</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>

                  {/* Position 8: 1:1 Time Tracker - Issue #1065 */}
                  <Grid size={{ xs: 12, md: 6 }} data-testid="child-card-one-on-one-time-tracker">
                    {(child.id || child.childId) && oneOnOneTimeData[(child.id || child.childId) as number] ? (
                      <OneOnOneTimeTrackerCard
                        data={oneOnOneTimeData[(child.id || child.childId) as number]}
                      />
                    ) : (
                      <Card data-testid="child-card-placeholder-weekly-one-on-one-time">
                        <CardContent>
                          <Typography variant="h6">Weekly 1:1 Time vs Developmental Averages</Typography>
                          <Typography color="text.secondary">Loading 1:1 time data...</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Grid>
                </Grid>
              </TabPanel>
            ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default NewAnalyticsPage;